import { Component, ElementRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { BaseToolComponent } from '../base-tool.component';
import { Rectangle } from 'src/app/tools/rectangle';
import { RatioUtil } from 'src/app/tools/ratio.utils';
import { CanvasEvent } from 'src/app/events/canvas.event';
import { ContextData } from 'src/app/data/context.data';
import { ApplicationData } from 'src/app/data/application.data';
import { ApplicationService } from 'src/app/services/application.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LabelData } from 'src/app/data/configurator/label/label.data';
import { LayerData, LAYER_TYPE } from 'src/app/data/configurator/layer/layer.data';
import { HistoryData } from 'src/app/data/configurator/history.data';
import { CommandData } from 'src/app/data/configurator/command.data';
import { AbstractLayer } from 'src/app/display/abstract-layer';
import { ImageLayer } from 'src/app/display/image-layer';

declare const fabric: any;

@Component({
  selector: 'label-canvas',
  templateUrl: './label-canvas.component.html',
  host: {
    class: 'label-canvas'
  }
})
export class LabelCanvasComponent extends BaseToolComponent {

  @ViewChild('canvas') canvasElement: ElementRef;

  @Input() set label(data: LabelData) {
    if ((this._labelData && this._labelData != data) || !this._labelData) {
      this.clearCurrentLabel();
      this._labelData = data as LabelData;
      this.setLabel();
    }
  }

  @Input() set layer(data: LayerData) {
    if (this._stage) {
      if (data) {
        if((ContextData.MODE === CanvasEvent.SET_CROP_MODE)){
          this.stopCropping(false);
          this.selectMode();
        }
        this._layerData = data;
        this._stage.forEachObject((obj) => {
          if (obj.data == data && this._stage.getActiveObject() != obj) {
            this._stage.setActiveObject(obj);
            this._stage.renderAll();
          }
        });
      } else {
        if (this._stage.getActiveObject()) {
          this._stage.discardActiveObject();
        }
      }
    }
  }

  @Input() set command(event: CanvasEvent) {
    if (event) {
      this.processCommand(event);
    }
  }
  @Output() onCommand: EventEmitter<CanvasEvent> = new EventEmitter();
  @Output() onRotation: EventEmitter<number> = new EventEmitter();

  private _checkInterval: any;

  private _drawingRatio: number;

  private _width:number;
  private _height: number;

  private _stage: any;
  private _masker: any;
  private _folds: any;
  private _indicators: any;
  private _widthInfo:any;
  private _heightInfo: any;

  private _workArea:Rectangle;
  private _currentScale: number = 1;
  private _currentRotation: number = 0;

  protected _subscription: Subscription;

  /**
	 * Constructor
	 */
  constructor(
    private applicationService:ApplicationService,
    private applicationData:ApplicationData,
    private elementRef: ElementRef,
    private translate: TranslateService,
  ) {
    super();

    //this._drawingRatio = window.devicePixelRatio;
    this._drawingRatio = 1;
  }

	/**
	 * On init
	 */
  ngOnInit() {
    fabric.Object.prototype.set({
      transparentCorners: false,
      borderColor: '#009fbc',
      borderOpacityWhenMoving: 1,
      cornerColor: '#009fbc'
    });

    this._stage = new fabric.Canvas("labelCanvas", { backgroundColor: "rgba(255, 255, 255, 1)" });
    this._stage.preserveObjectStacking = true;
    this._stage.selection = false;

    (window as any).toto = this._stage;

    this._widthInfo = new fabric.IText('', {
      evented: false,
      fontSize: 14,
      fill: '#878787',
      fontFamily: 'Exo 2',
      originX: 'center',
      originY: 'center'
    });
    this._stage.add(this._widthInfo);

    this._heightInfo = new fabric.IText('', {
      evented: false,
      fontSize: 14,
      fill: '#878787',
      fontFamily: 'Exo 2',
      originX: 'center',
      originY: 'center'
    });
    this._stage.add(this._heightInfo);

    this._stage.on('object:selected', (e:any) => {
      if(ContextData.MODE === CanvasEvent.SET_CROP_MODE) return;
      this.applicationService.currentLayerData = e.target.data;
    });

    this._stage.on('selection:updated', (e: any) => {
      if(ContextData.MODE === CanvasEvent.SET_CROP_MODE) return;
      this.applicationService.currentLayerData = e.target.data;
    });

    this._stage.on('selection:cleared', (e: any) => {
      Promise.resolve(null).then(() => this.applicationService.currentLayerData = null);
    });

    let pausePanning = false;
    let lastX = 0;
    let lastY = 0;
    let state = 'idle';
    this._stage.on({
      'mouse:wheel': (event) => {
        // console.log("LabelCanvasComponent -- mouse:wheel");
        event.e.preventDefault();

        var point = new fabric.Point(event.pointer.x, event.pointer.y);

        let delta = event.e.wheelDelta || -event.e.detail || -(event.e.deltaY * 25)


        let zoom = this._stage.getZoom() * (1 + 0.01 * (delta / Math.abs(delta)));

        this.updateScale(zoom, point);
      },
      'object:selected': (event) => {
        // console.log("LabelCanvasComponent -- object:selected", event);
        if (ContextData.MODE !== CanvasEvent.SET_CROP_MODE) {
          pausePanning = true;
        } else {
          // event.e.preventDefault();
        }
      },
      'selection:cleared': (event) => {
        // console.log("LabelCanvasComponent -- selection:cleared", event);
        if(event.deselected && event.deselected[0].cornerColor== '#36fd00'){
          this.stopCropping(false);
          this.selectMode();
        }
        pausePanning = false;
      },
      'mouse:down': (event) => {
        // console.log("LabelCanvasComponent -- mouse:down");
        if (ContextData.MODE == CanvasEvent.SET_PAN_MODE) {
          state = 'panning';
          lastX = event.e.clientX;
          lastY = event.e.clientY;
        }

        if (ContextData.MODE == CanvasEvent.SET_ZOOM_IN_MODE) {
          this.updateScale(this._stage.getZoom() * ContextData.ZOOM_FACTOR, new fabric.Point(event.pointer.x, event.pointer.y));
        }

        if (ContextData.MODE == CanvasEvent.SET_ZOOM_OUT_MODE) {
          this.updateScale(this._stage.getZoom() / ContextData.ZOOM_FACTOR, new fabric.Point(event.pointer.x, event.pointer.y));
        }
      },
      'mouse:up': (event) => {
        // console.log("LabelCanvasComponent -- mouse:up");
        state = 'idle';
      },
      'mouse:move': (event) => {
        // console.log("LabelCanvasComponent -- mouse:move");
        if (pausePanning == false && state === 'panning' && event && event.e) {
          let deltaX = 0;
          let deltaY = 0;
          if (lastX) {
            deltaX = event.e.clientX - lastX;
          }
          if (lastY) {
            deltaY = event.e.clientY - lastY;
          }
          // Update the last X and Y values
          lastX = event.e.clientX;
          lastY = event.e.clientY;

          let delta = new fabric.Point(deltaX, deltaY);
          this._stage.relativePan(delta);
          this._stage.trigger('moved');

          this.redraw();
        }
      }
    });

    this.draw();
    setTimeout(() => this.drawLayers(), 100);

    this._checkInterval = setInterval(() => {
      this.checkWidth();
    }, 300);
  }

  /**
   * On check
   */
  ngDoCheck() {
    this.checkWidth();
  }

  checkWidth() {
    if (this.elementRef.nativeElement.offsetWidth * this._drawingRatio != this._width || this.elementRef.nativeElement.offsetHeight * this._drawingRatio != this._height) {
      this._width = this.elementRef.nativeElement.offsetWidth * this._drawingRatio;
      this._height = this.elementRef.nativeElement.offsetHeight * this._drawingRatio;
      this._stage.setWidth(this._width);
      this._stage.setHeight(this._height);

      let toolbar = document.getElementsByTagName('tools-bar');
      let toolbarHeight: number = 0;
      if (toolbar.length > 0) {
        toolbarHeight = (<HTMLElement>toolbar[0]).offsetHeight;
      }
      this._workArea = new Rectangle(0, 0, this._width, this._height - toolbarHeight);

      this.fitCanvas();
      this.centerCanvas();
    }
  }

  /**
	 * Display label
	 */
  protected clearCurrentLabel(): void {
    super.clearCurrentLabel();
    if((ContextData.MODE === CanvasEvent.SET_CROP_MODE)){
      // console.log("clearCurrentLabel()")
      this.stopCropping(false);
      this.selectMode();
    }
    this._stage && this._stage.remove(...this._stage.getObjects().concat());
  }
  protected setLabel(): void {
    if (this._stage) {
      this.draw();
      this.applyCanvasRotation();
    }

    if (this._labelData) {
      this._labelData.label.stage = this._stage;
      this.drawLayers();
    }

    super.setLabel();
  }

  /**
 	 *  Label updated
 	 */
  protected labelUpdated(data: LayerData): void {
    super.labelUpdated(data);

    if (this._stage) {
      setTimeout(() => {
        this._stage.renderAll();
      }, 10);
    }
  }

	/**
 	 *  Label updated
 	 */
  protected labelLayerAdded(data: LayerData): void {
    super.labelLayerAdded(data);

    if (this._stage) {
      setTimeout(() => {
        this.drawLayers();

        this._stage.forEachObject((obj) => {
          if (obj.data == data) {
            // console.log("setActive",obj);
            this._stage.setActiveObject(obj);
            this._stage.renderAll();
          }
        });
      }, 150);
    }
  }

	/**
 	 *  Label updated
 	 */
  protected labelLayerRemoved(data: LayerData): void {
    super.labelLayerRemoved(data);

    if (this._stage) {
      this._stage.forEachObject((obj) => {
        if (obj.data == data) {
          this._stage.remove(obj);
        }
      });
    }
  }

  /**
 	 *  Label updated
 	 */
  protected labelLayersReordered(): void {
    super.labelLayersReordered();
    this.drawLayers();
  }

  /**
   * Draw
   */
  draw() {
    if (this._stage) {
      this._stage.remove(this._stage.getObjects());
      this._stage.add(this._labelData.templateData.printedArea);
      this._stage.add(this._labelData.templateData.border);



      this._widthInfo.set({
        text: '' + (Math.floor(this._labelData.width * 10) / 10) + ' ' + this.translate.instant('cfg_mm'),
      });

      this._heightInfo.set({
        text: '' + (Math.floor(this._labelData.height * 10) / 10) + ' ' + this.translate.instant('cfg_mm'),
      });
    }

  }

  /**
  * Draw layers
  */
  drawLayers() {
    // console.log("LabelCanvasComponent.drawLayers()");
    if (this._stage) {

      for (let l of this._labelData.label.layers) {
        if (l && l.display) {
          if (!this._stage.contains(l.display)) this._stage.add(l.display);
        }
      }

      this._stage.bringToFront(this._labelData.templateData.printedArea);
      this._stage.bringToFront(this._labelData.templateData.border);
      this._stage.bringToFront(this._indicators);
      this._stage.bringToFront(this._widthInfo);
      this._stage.bringToFront(this._heightInfo);

      this.redraw();
    }
  }

  /**
   * Redraw UI
   */
  redraw() {

    if (this._stage) {

      // console.log("LabelCanvasComponent.redraw()");

      this._stage.remove(this._masker);
      this._stage.remove(this._indicators);
      this._stage.remove(this._folds);

      this._labelData.templateData.border.set({
        strokeWidth: 1 / this._stage.getZoom(),
      });


      this._labelData.templateData.shapeMasker.set({
        top: (this._stage.viewportTransform[5] - this._height / 2) / this._stage.getZoom(),
        left: (this._stage.viewportTransform[4] - this._width / 2) / this._stage.getZoom()
      });

      let _greyedArea = new fabric.Rect({
        width: this._width / this._stage.getZoom() ,
        height: this._height / this._stage.getZoom(),
        top: -(this._stage.viewportTransform[5]) / this._stage.getZoom(),
        left: -(this._stage.viewportTransform[4]) / this._stage.getZoom(),
        fill: '#e9e9e9'
      });

      let _folds = [];
      for (let i = 0; i < this._labelData.templateData.folds.length; i++) {
        let _line = new fabric.Line([], {
          strokeWidth: 1 / this._stage.getZoom(),
          fill: '#878787',
          stroke: '#878787',
          strokeDashArray: [3/ this._stage.getZoom(), 10/ this._stage.getZoom(), 10/ this._stage.getZoom(), 3/ this._stage.getZoom()]
        });
        _folds.push(_line);
      }

      this._masker = new fabric.Group([
        _greyedArea,

      ],{
        evented: false,
        clipPath: this._labelData.templateData.shapeMasker,
        opacity: 0.8
      });
      this._stage.add(this._masker);
      this._masker.setCoords();

      let line1 = new fabric.Line([], {
        fill: '#878787',
        stroke: '#878787',
        strokeWidth: 1 / this._stage.getZoom(),
      });

      let line2 = new fabric.Line([], {
        fill: '#878787',
        stroke: '#878787',
        strokeWidth: 1 / this._stage.getZoom(),
      });

      this._widthInfo.set({
        scaleX: 1 / this._stage.getZoom(),
        scaleY: 1 / this._stage.getZoom(),
      });

      this._heightInfo.set({
        scaleX: 1 / this._stage.getZoom(),
        scaleY: 1 / this._stage.getZoom(),
      });

      if (this._currentRotation % 360 == 0) {

        for (let i = 0; i < _folds.length; i++) {
          const fold = this._labelData.templateData.folds[i];
          _folds[i].set({
            'x1': fold.xFrom,
            'y1': fold.yFrom,
            'x2': fold.xTo,
            'y2': fold.yTo
          });
        }


        this._labelData.templateData.border.set({
          top: -1 / this._stage.getZoom(),
          left: -1 / this._stage.getZoom(),
        });

        this._labelData.templateData.printedArea.set({
          top: -1 / this._stage.getZoom(),
          left: -1 / this._stage.getZoom(),
        });

        line1.set({
          'x1': 0,
          'y1': -20 / this._stage.getZoom(),
          'x2': this._labelData.templateData.shape.width,
          'y2': -20 / this._stage.getZoom()
        });

        line2.set({
          'x1': - 20 / this._stage.getZoom(),
          'y1': 0,
          'x2': - 20 / this._stage.getZoom(),
          'y2': this._labelData.templateData.shape.height
        });

        this._widthInfo.set({
          angle: 0,
          left: this._labelData.templateData.shape.width / 2,
          top: - 30 / this._stage.getZoom(),
        });

        this._heightInfo.set({
          angle: -90,
          left: -30 / this._stage.getZoom(),
          top: this._labelData.templateData.shape.height / 2,
        });
      } else if (this._currentRotation % 270 == 0) {


        for (let i = 0; i < _folds.length; i++) {
          const fold = this._labelData.templateData.folds[i];
          _folds[i].set({
            'x1': fold.yFrom,
            'y1': -fold.xFrom,
            'x2': fold.yTo,
            'y2': -fold.xTo
          });
        }

        this._labelData.templateData.border.set({
          top: 1 / this._stage.getZoom(),
          left: -1 / this._stage.getZoom(),
        });

        this._labelData.templateData.printedArea.set({
          top: 1 / this._stage.getZoom(),
          left: -1 / this._stage.getZoom(),
        });

        line1.set({
          'x1': -20 / this._stage.getZoom(),
          'y1': 0,
          'x2': -20 / this._stage.getZoom(),
          'y2': -this._labelData.templateData.shape.width
        });

        line2.set({
          'x1': 0,
          'y1': -this._labelData.templateData.shape.width - 20 / this._stage.getZoom(),
          'x2': this._labelData.templateData.shape.height,
          'y2': -this._labelData.templateData.shape.width - 20 / this._stage.getZoom(),
        });

        this._widthInfo.set({
          angle: -90,
          left: -30 / this._stage.getZoom(),
          top: -this._labelData.templateData.shape.width / 2,
        });

        this._heightInfo.set({
          angle: 0,
          left: this._labelData.templateData.shape.height / 2,
          top: -this._labelData.templateData.shape.width - 30 / this._stage.getZoom(),
        });
      } else if (this._currentRotation % 180 == 0) {

        for (let i = 0; i < _folds.length; i++) {
          const fold = this._labelData.templateData.folds[i];
          _folds[i].set({
            'x1': -fold.xFrom,
            'y1': -fold.yFrom,
            'x2': -fold.xTo,
            'y2': -fold.yTo
          });
        }

        this._labelData.templateData.border.set({
          top: 1 / this._stage.getZoom(),
          left: 1 / this._stage.getZoom(),
        });

        this._labelData.templateData.printedArea.set({
          top: 1 / this._stage.getZoom(),
          left: 1 / this._stage.getZoom(),
        });

        line1.set({
          'x1': -this._labelData.templateData.shape.width,
          'y1': -this._labelData.templateData.shape.height - 20 / this._stage.getZoom(),
          'x2': 0,
          'y2': -this._labelData.templateData.shape.height - 20 / this._stage.getZoom()
        });

        line2.set({
          'x1': -20 / this._stage.getZoom() - this._labelData.templateData.shape.width,
          'y1': 0,
          'x2': -20 / this._stage.getZoom() - this._labelData.templateData.shape.width,
          'y2': -this._labelData.templateData.shape.height,
        });

        this._widthInfo.set({
          angle: 0,
          left: -this._labelData.templateData.shape.width / 2,
          top: - this._labelData.templateData.shape.height - 30 / this._stage.getZoom(),
        });

        this._heightInfo.set({
          angle: -90,
          left: -this._labelData.templateData.shape.width -30 / this._stage.getZoom(),
          top: -this._labelData.templateData.shape.height / 2,
        });
      } else if (this._currentRotation % 90 == 0) {

        for (let i = 0; i < _folds.length; i++) {
          const fold = this._labelData.templateData.folds[i];
          _folds[i].set({
            'x1': -fold.yFrom,
            'y1': fold.xFrom,
            'x2': -fold.yTo,
            'y2': fold.xTo
          });
        }

        this._labelData.templateData.border.set({
          top: -1 / this._stage.getZoom(),
          left: 1 / this._stage.getZoom(),
        });
        this._labelData.templateData.printedArea.set({
          top: -1 / this._stage.getZoom(),
          left: 1 / this._stage.getZoom(),
        });

        line1.set({
          'x1': -this._labelData.templateData.shape.height - 20 / this._stage.getZoom(),
          'y1': 0,
          'x2': -this._labelData.templateData.shape.height - 20 / this._stage.getZoom(),
          'y2': this._labelData.templateData.shape.width,
        });

        line2.set({
          'x1': 0,
          'y1': -20 / this._stage.getZoom(),
          'x2': -this._labelData.templateData.shape.height,
          'y2': -20 / this._stage.getZoom(),
        });

        this._widthInfo.set({
          angle: -90,
          left: -this._labelData.templateData.shape.height - 30 / this._stage.getZoom(),
          top: this._labelData.templateData.shape.width / 2,
        });

        this._heightInfo.set({
          angle: 0,
          left: -this._labelData.templateData.shape.height / 2,
          top: -30 / this._stage.getZoom(),
        });
      }


      this._folds = new fabric.Group(_folds,{
        evented: false
      });
      this._stage.add(this._folds);


      this._indicators = new fabric.Group([line1, line2], {
        evented: false,
      });

      this._stage.add(this._indicators);

      this._widthInfo.setCoords();
      this._heightInfo.setCoords();

      this._stage.bringToFront(this._indicators);
      this._stage.bringToFront(this._widthInfo);
      this._stage.bringToFront(this._heightInfo);
      this._stage.bringToFront(this._labelData.templateData.printedArea);
      this._stage.bringToFront(this._labelData.templateData.border);
      this._stage.bringToFront(this._folds);

      this._stage.renderAll();

      this.updateLabelScreenCoord();
    }
  }

  public updateLabelScreenCoord():void{
    if(!this.applicationData.configuration.currentLabelScreenRect) this.applicationData.configuration.currentLabelScreenRect = new Rectangle();
    this.applicationData.configuration.currentLabelScreenRect.x = this._stage._offset.left + this._stage.viewportTransform[4];
    this.applicationData.configuration.currentLabelScreenRect.y = this._stage._offset.top + this._stage.viewportTransform[5];

    if (this._currentRotation % 180 == 0) {
      this.applicationData.configuration.currentLabelScreenRect.width = this._labelData.pixelsWidth * this._currentScale;
      this.applicationData.configuration.currentLabelScreenRect.height = this._labelData.pixelsHeight * this._currentScale;
    } else if (this._currentRotation != 0 && this._currentRotation % 90 == 0) {
      this.applicationData.configuration.currentLabelScreenRect.width = this._labelData.pixelsHeight * this._currentScale;
      this.applicationData.configuration.currentLabelScreenRect.height = this._labelData.pixelsWidth * this._currentScale;
    }

    if (this._currentRotation % 360 == 0) {
      // rien à changer
    } else if (this._currentRotation % 270 == 0) {
      this.applicationData.configuration.currentLabelScreenRect.y -= this.applicationData.configuration.currentLabelScreenRect.height;
    } else if (this._currentRotation % 180 == 0) {
      this.applicationData.configuration.currentLabelScreenRect.x -= this.applicationData.configuration.currentLabelScreenRect.width;
      this.applicationData.configuration.currentLabelScreenRect.y -= this.applicationData.configuration.currentLabelScreenRect.height;
    } else if (this._currentRotation % 90 == 0) {
      this.applicationData.configuration.currentLabelScreenRect.x -= this.applicationData.configuration.currentLabelScreenRect.width;
    }
  }

  /**
		* Fit canvas
		*/
  protected fitCanvas(): void {
    var size: any;
    var ratio: number;

    if (this._currentRotation % 180 == 0) {
      size = RatioUtil.scaleToFit(new Rectangle(0, 0, this._labelData.pixelsWidth, this._labelData.pixelsHeight), new Rectangle(30, 30, this._workArea.width - 30 - 40, this._workArea.height - 30 - 40));
      ratio = size.width / this._labelData.pixelsWidth;
    } else if (this._currentRotation != 0 && this._currentRotation % 90 == 0) {
      size = RatioUtil.scaleToFit(new Rectangle(0, 0, this._labelData.pixelsHeight, this._labelData.pixelsWidth), new Rectangle(30, 30, this._workArea.width - 30 - 40, this._workArea.height - 30 - 40));
      ratio = size.width / this._labelData.pixelsHeight;
    }


    this.updateScale(ratio);
  }

  /**
		* Center canvas
		*/
  protected centerCanvas(): void {
    if (this._currentRotation != 0 && this._currentRotation % 270 == 0) {
      this._stage.absolutePan(new fabric.Point(-Math.round((this._workArea.width - this._labelData.pixelsHeight * this._currentScale) * 0.5) - 20 * this._currentScale, -Math.round((this._workArea.height + this._labelData.pixelsWidth * this._currentScale) / 2) - 20 * this._currentScale));
    } else if (this._currentRotation != 0 && this._currentRotation % 180 == 0) {
      this._stage.absolutePan(new fabric.Point(-Math.round((this._workArea.width + this._labelData.pixelsWidth * this._currentScale) * 0.5) - 20 * this._currentScale, -Math.round((this._workArea.height + this._labelData.pixelsHeight * this._currentScale) / 2) - 20 * this._currentScale));
    } else if (this._currentRotation != 0 && this._currentRotation % 90 == 0) {
      this._stage.absolutePan(new fabric.Point(-Math.round((this._workArea.width + this._labelData.pixelsHeight * this._currentScale) * 0.5) - 20 * this._currentScale, -Math.round((this._workArea.height - this._labelData.pixelsWidth * this._currentScale) / 2) - 20 * this._currentScale));
    } else {
      this._stage.absolutePan(new fabric.Point(-Math.round((this._workArea.width - this._labelData.pixelsWidth * this._currentScale) * 0.5) - 20 * this._currentScale, -Math.round((this._workArea.height - this._labelData.pixelsHeight * this._currentScale) / 2) - 20 * this._currentScale));
    }

    this.redraw();
  }

  /**
   * Update scale
   */
  private updateScale(scale: number, point:any = null):void {
    scale = Math.min(Math.max(scale, 0.001), 1);
    this._currentScale = scale;
    if (point) {
      this._stage.zoomToPoint(point, scale);
    } else {
      this._stage.setZoom(scale);
    }

    this.redraw();
  }



  protected processCommand(event:CanvasEvent): void {
		if(
      (ContextData.MODE === CanvasEvent.SET_CROP_MODE) &&
      (event.type === CanvasEvent.SET_CROP_MODE)
      ) {
        this.selectMode();
        this.stopCropping(true);

    } else {
      super.processCommand(event);
    }


  }

  /**
	 * Select mode
	 */
  protected selectMode(): void {

    ContextData.MODE = CanvasEvent.SET_SELECT_MODE;

    this._stage.getObjects().forEach((obj, index) => {
       obj.evented = true;
    });

    this._indicators.evented = false;
    this._labelData.templateData.printedArea.evented = false;
    this._labelData.templateData.border.evented = false;
    this._labelData.templateData.shapeMasker.evented = false;
    this._masker.evented = false;
    this._heightInfo.evented = false;
    this._widthInfo.evented = false;
    this._stage.defaultCursor = 'default';
  }

  /**
	 * pan mode
	 */
  protected panMode(): void {

    ContextData.MODE = CanvasEvent.SET_PAN_MODE;

    // console.log("discardActiveObject");
    this._stage.discardActiveObject();

    this._stage.getObjects().forEach((obj, index) => {
      obj.evented = false;
    });
    this._stage.defaultCursor = 'move';

    this._stage.renderAll();

  }

  /**
	 * Zoom in mode
	 */
  protected zoomInMode(): void {
    ContextData.MODE = CanvasEvent.SET_ZOOM_IN_MODE;

    // console.log("discardActiveObject");
    this._stage.discardActiveObject();

    this._stage.getObjects().forEach((obj, index) => {
      obj.evented = false;
    });
    this._stage.defaultCursor = 'zoom-in';

    this._stage.renderAll();
  }

  /**
	 * Zoom out mode
	 */
  protected zoomOutMode(): void {
    ContextData.MODE = CanvasEvent.SET_ZOOM_OUT_MODE;

    // console.log("discardActiveObject");
    this._stage.discardActiveObject();

    this._stage.getObjects().forEach((obj, index) => {
      obj.evented = false;
    });
    this._stage.defaultCursor = 'zoom-out';

    this._stage.renderAll();
  }

  /**
	 * Rotate canvas
	 */
  protected rotateCanvas(): void {
    this._currentRotation = (this._currentRotation + 90) % 360;
    this.applicationData.configuration.currentCanvasRotation = this._currentRotation;
    this.onRotation.emit(this._currentRotation);

    this.applyCanvasRotation();
  }

  /**
	 * Rotate canvas
	 */
  private applyCanvasRotation(): void {
    this._labelData.templateData.printedArea.angle = this._currentRotation;
    this._labelData.templateData.printedArea.setCoords();
    this._labelData.templateData.border.angle = this._currentRotation;
    this._labelData.templateData.border.setCoords();
    this._labelData.templateData.shapeMasker.angle = this._currentRotation;
    this._labelData.templateData.shapeMasker.setCoords();
    this._labelData.templateData.shape.angle = this._currentRotation;
    this._labelData.templateData.shape.setCoords();
    this._labelData.label.rotation = this._currentRotation;


    this.fitCanvas();
    this.centerCanvas();
  }

  /**
   * Center layer horizontally
   */
  protected centerLayerHorizontally(): void {
    if (this._layerData) {
      let positionData = { x: this._layerData.x, y: this._layerData.y, width: this._layerData.width, height: this._layerData.height, rotation: this._layerData.rotation };
      HistoryData.add(this._labelData, this._layerData, CommandData.SET_POSITION, positionData);

      this._stage.forEachObject((obj) => {
        if (obj.data == this._layerData) {


          var bound = obj.getBoundingRect(true, true);

          var deltaLeft = obj.left-bound.left;


          if (this._currentRotation % 360 == 0) {
            this._layerData.x = this.labelData.pixelsWidth*.5 - bound.width*.5 + deltaLeft
          } else if (this._currentRotation % 270 == 0) {
            this._layerData.y = this.labelData.pixelsHeight*.5 - bound.width*.5 + deltaLeft;
          } else if (this._currentRotation % 180 == 0) {
            this._layerData.x = this.labelData.pixelsWidth*.5 + bound.width*.5 - deltaLeft;
          } else if (this._currentRotation % 90 == 0) {
            this._layerData.y = this.labelData.pixelsHeight*.5 + bound.width*.5 - deltaLeft;
          }


          this._layerData.invalidate();
        }
      });
    }
  }

  stopCropping(applyCrop:boolean){

    for (let i = 0; i < this.applicationData.configuration.currentLabel.label.layers.length; i++) {
      const layer:AbstractLayer = this.applicationData.configuration.currentLabel.label.layers[i];
      if(layer.data.type == LayerData.IMAGE){
        if((<ImageLayer>layer).cropMode){
          (<ImageLayer>layer).exitCropMode(this._stage, applyCrop);
          // return layer;
        }
      }
    }
  }

  /**
	 * Center layer vertically
	 */
  protected cropMode(): void {

    if (!this._layerData) return;
    if (this._layerData.type !== LayerData.IMAGE || this._layerData.special == "background") return;

    this._stage.discardActiveObject();

    this._stage.getObjects().forEach((obj, index) => {
      obj.evented = false;
    });

    this.applicationData.configuration.currentLabel.label.layers.map((layer:AbstractLayer)=>{
      if (layer.data == this._layerData) {
        if(ContextData.MODE !== CanvasEvent.SET_CROP_MODE){
          ContextData.MODE = CanvasEvent.SET_CROP_MODE;
          (<ImageLayer>layer).enterCropMode(this._stage);
        }
      }
    })

  }

	/**
	 * Center layer vertically
	 */
  protected centerLayerVertically(): void {
    if (this._layerData) {
      let positionData = { x: this._layerData.x, y: this._layerData.y, width: this._layerData.width, height: this._layerData.height, rotation: this._layerData.rotation };
      HistoryData.add(this._labelData, this._layerData, CommandData.SET_POSITION, positionData);

      this._stage.forEachObject((obj) => {
        if (obj.data == this._layerData) {


          var bound = obj.getBoundingRect(true, true);
          var deltaTop = obj.top-bound.top;


          if (this._currentRotation % 360 == 0) {
            this._layerData.y = this.labelData.pixelsHeight*.5 - bound.height*.5 + deltaTop
          } else if (this._currentRotation % 270 == 0) {
            this._layerData.x = this.labelData.pixelsWidth*.5 + bound.height*.5 - deltaTop;
          } else if (this._currentRotation % 180 == 0) {
            this._layerData.y = this.labelData.pixelsHeight*.5 + bound.height*.5 - deltaTop;
          } else if (this._currentRotation % 90 == 0) {
            this._layerData.x = this.labelData.pixelsWidth*.5 - bound.height*.5 + deltaTop;
          }

          this._layerData.invalidate();
        }
      });
    }
  }


	/**
	* On destroy
	*/
  ngOnDestroy() {
    super.ngOnDestroy();

    if (this._checkInterval) {
      clearInterval(this._checkInterval);
    }
  }
}
